import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, ArrowLeft, Check, Sparkles, Shield, Clock, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BENEFITS = [
  { icon: Sparkles, text: "AI-genererede trænings- og madplaner" },
  { icon: Dumbbell, text: "85+ øvelser med video-guides" },
  { icon: Shield, text: "Ingen binding - afmeld når som helst" },
];

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // "form" or "redirecting"
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Adgangskode skal være mindst 6 tegn"); return; }
    setLoading(true);
    
    try {
      // Step 1: Create account - get token directly from response
      const registerData = await register(name, email, password);
      const newToken = registerData.token;
      
      toast.success("Konto oprettet! Omdirigerer til betaling...");
      setStep("redirecting");
      
      // Step 2: Redirect to Stripe checkout for trial - use token directly
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/start-trial`, 
        { origin_url: origin }, 
        { headers: { Authorization: `Bearer ${newToken}` } }
      );
      
      if (res.data.url) {
        // Redirect to Stripe
        window.location.href = res.data.url;
      } else {
        throw new Error("Kunne ikke oprette checkout session");
      }
    } catch (err) {
      setStep("form");
      toast.error(err.response?.data?.detail || "Fejl ved registrering");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA3ODB8MHwxfHNlYXJjaHwxfHxneW0lMjBkYXJrfGVufDB8fHx8MTcwOTMwMDAwMHww&ixlib=rb-4.0.3&q=85"
            alt="Gym"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg bg-[#DFFF00] flex items-center justify-center">
              <Dumbbell className="h-6 w-6 text-black" />
            </div>
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-4xl xl:text-5xl font-bold uppercase text-white leading-tight mb-6">
            Start din<br />
            <span className="text-[#DFFF00]">transformation</span><br />
            i dag
          </h2>

          <p className="text-zinc-400 text-lg mb-10 max-w-md">
            Få adgang til Danmarks mest komplette fitness platform med AI-drevne programmer.
          </p>

          <div className="space-y-4">
            {BENEFITS.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#DFFF00]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-[#DFFF00]" />
                </div>
                <span className="text-zinc-300">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-zinc-500 hover:text-white transition-colors text-sm" data-testid="register-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
          </Link>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <Dumbbell className="h-6 w-6 text-[#DFFF00]" />
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          {/* Trial Badge - PROMINENT */}
          <div className="mb-8 p-4 bg-gradient-to-r from-[#DFFF00]/10 to-[#DFFF00]/5 border border-[#DFFF00]/20 rounded-lg" data-testid="trial-badge">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-[#DFFF00]" />
              </div>
              <div>
                <p style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase text-[#DFFF00]">
                  3 Dage Gratis
                </p>
                <p className="text-sm text-zinc-400">
                  Afmeld når som helst inden trial udløber
                </p>
              </div>
            </div>
          </div>

          {step === "redirecting" ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 text-[#DFFF00] animate-spin mx-auto mb-4" />
              <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-2xl font-bold text-white mb-2">
                Omdirigerer til betaling...
              </h2>
              <p className="text-zinc-400">Du sendes til Stripe for at indtaste kortoplysninger</p>
            </div>
          ) : (
            <>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-2" data-testid="register-title">
                Opret din konto
              </h1>
              <p className="text-zinc-400 mb-8">
                Start din 3-dages gratis prøveperiode.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Navn</Label>
                  <Input 
                    id="name" 
                    type="text" 
                    value={name} 
                    onChange={e => setName(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="register-name" 
                    placeholder="Dit fulde navn" 
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="register-email" 
                    placeholder="din@email.dk" 
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Adgangskode</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="register-password" 
                    placeholder="Mindst 6 tegn" 
                  />
                </div>

                {/* Card info notice */}
                <div className="flex items-center gap-2 p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg">
                  <CreditCard className="h-5 w-5 text-zinc-500 flex-shrink-0" />
                  <p className="text-xs text-zinc-500">
                    Du indtaster kortoplysninger på næste trin via Stripe. Ingen opkrævning i prøveperioden.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading} 
                  data-testid="register-submit"
                  className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.3)] rounded-lg font-bold uppercase tracking-wider h-14 text-base transition-all duration-300"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Opretter konto...</>
                  ) : (
                    "Start gratis prøveperiode"
                  )}
                </Button>
              </form>

              {/* What you get */}
              <div className="mt-8 pt-8 border-t border-zinc-800">
                <p className="text-xs uppercase tracking-wider text-zinc-600 mb-4">Inkluderet i din prøveperiode</p>
                <div className="grid grid-cols-2 gap-3">
                  {["AI træningsprogrammer", "AI madplaner", "85+ øvelser", "Krops-tracker", "Vand-tracker", "Fuld adgang"].map(item => (
                    <div key={item} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-[#DFFF00] flex-shrink-0" />
                      <span className="text-sm text-zinc-400">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-8 text-center text-sm text-zinc-500">
                Har du allerede en konto?{" "}
                <Link to="/login" className="text-[#DFFF00] hover:underline font-medium" data-testid="register-login-link">
                  Log ind her
                </Link>
              </p>

              <p className="mt-4 text-center text-xs text-zinc-600">
                Ved at oprette en konto accepterer du vores vilkår.
                <br />Efter prøveperioden: 49 kr/md. Afmeld når som helst.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
